import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { helper } from 'src/environments/helper';
import { AuditLogsService, AuditLogItem, MetaOption } from 'src/app/services/audit-logs.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-audit-logs',
    templateUrl: './audit-logs.component.html',
    styleUrls: ['./audit-logs.component.scss'],
})
export class AuditLogsComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isExporting = false;

    // Filters
    dateFrom: string = '';
    dateTo: string = '';
    moduleFilter: string = '';
    actionFilter: string = '';
    userNameFilter: string = '';
    resultFilter: string = '';
    searchFilter: string = '';

    // Dropdown options
    modules: MetaOption[] = [];
    actions: MetaOption[] = [];
    results: MetaOption[] = [];

    // Detail modal
    selectedLog: AuditLogItem | null = null;
    showDetailModal = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();

    constructor(
        private auditLogsService: AuditLogsService
    ) {}

    ngOnInit(): void {
        this.loadFilterOptions();
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    loadFilterOptions(): void {
        // Load modules
        this.auditLogsService.getModules().subscribe({
            next: (response) => {
                if (response.success && response.data) {
                    this.modules = response.data;
                }
            },
            error: (err) => console.error('Error loading modules:', err)
        });

        // Load actions
        this.auditLogsService.getActions().subscribe({
            next: (response) => {
                if (response.success && response.data) {
                    this.actions = response.data;
                }
            },
            error: (err) => console.error('Error loading actions:', err)
        });

        // Load results
        this.auditLogsService.getResults().subscribe({
            next: (response) => {
                if (response.success && response.data) {
                    this.results = response.data;
                }
            },
            error: (err) => console.error('Error loading results:', err)
        });
    }

    renderDT(): void {
        const self = this;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,
            order: [[0, 'desc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                self.isLoading = true;

                const params: any = {
                    draw: dataTablesParameters.draw,
                    start: dataTablesParameters.start,
                    length: dataTablesParameters.length,
                };

                // Sorting
                if (dataTablesParameters.order && dataTablesParameters.order.length > 0) {
                    const orderCol = dataTablesParameters.order[0].column;
                    const orderDir = dataTablesParameters.order[0].dir;
                    const columns = ['id', 'created_at', 'user_name', 'module', 'action', 'model_name', 'result'];
                    params.sort_by = columns[orderCol] || 'id';
                    params.sort_dir = orderDir;
                }

                // Filters
                if (self.dateFrom) params.date_from = self.dateFrom;
                if (self.dateTo) params.date_to = self.dateTo;
                if (self.moduleFilter) params.module = self.moduleFilter;
                if (self.actionFilter) params.action = self.actionFilter;
                if (self.userNameFilter) params.user_name = self.userNameFilter;
                if (self.resultFilter) params.result = self.resultFilter;
                if (self.searchFilter) params.search = self.searchFilter;

                self.auditLogsService.getAuditLogs(params).subscribe({
                    next: (resp: any) => {
                        self.isLoading = false;
                        callback({
                            recordsTotal: resp.recordsTotal || 0,
                            recordsFiltered: resp.recordsFiltered || 0,
                            data: resp.data || [],
                        });
                    },
                    error: (err) => {
                        self.isLoading = false;
                        console.error('Error fetching audit logs:', err);
                        callback({
                            recordsTotal: 0,
                            recordsFiltered: 0,
                            data: [],
                        });
                    }
                });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                    width: '50px',
                },
                {
                    title: 'Data',
                    data: 'created_at',
                    width: '140px',
                    render: (data: string) => {
                        if (!data) return '-';
                        const date = new Date(data);
                        return date.toLocaleString('ro-RO', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        });
                    }
                },
                {
                    title: 'Utilizator',
                    data: 'user_name',
                    width: '180px',
                    render: (data: string, type: string, row: AuditLogItem) => {
                        let html = `<strong>${data || '-'}</strong>`;
                        if (row.functie_name) {
                            html += `<br><small class="text-info">${row.functie_name}</small>`;
                        }
                        if (row.directie_name) {
                            html += `<br><small class="text-muted">${row.directie_name}</small>`;
                        }
                        return html;
                    }
                },
                {
                    title: 'Modul',
                    data: 'module',
                    width: '120px',
                    render: (data: string) => {
                        if (!data) return '-';
                        const parts = data.split('.');
                        return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, ' ')).join(' > ');
                    }
                },
                {
                    title: 'Acțiune',
                    data: 'action_label',
                    width: '100px',
                    render: (data: string, type: string, row: AuditLogItem) => {
                        const colorMap: { [key: string]: string } = {
                            'create': 'success',
                            'update': 'warning',
                            'delete': 'danger',
                            'status_change': 'info',
                            'view': 'secondary',
                            'export': 'primary'
                        };
                        const color = colorMap[row.action] || 'secondary';
                        return `<span class="badge bg-${color}">${data}</span>`;
                    }
                },
                {
                    title: 'Obiect',
                    data: 'model_name',
                    width: '180px',
                    render: (data: string, type: string, row: AuditLogItem) => {
                        let html = data ? `<strong>${data}</strong>` : '-';
                        if (row.short_model_type) {
                            html += `<br><small class="text-muted">${row.short_model_type} #${row.model_id}</small>`;
                        }
                        return html;
                    }
                },
                {
                    title: 'Rezultat',
                    data: 'result_label',
                    width: '80px',
                    render: (data: string, type: string, row: AuditLogItem) => {
                        const color = row.result === 'success' ? 'success' : 'danger';
                        return `<span class="badge bg-${color}">${data}</span>`;
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    width: '80px',
                    orderable: false,
                    render: (data: any, type: string, row: AuditLogItem) => {
                        let html = `<button class="btn btn-sm btn-info view-details" data-id="${row.id}" title="Detalii">
                            <i class="fa fa-eye"></i>
                        </button>`;
                        if (row.has_diff) {
                            html += ` <button class="btn btn-sm btn-warning view-diff" data-id="${row.id}" title="Diferențe">
                                <i class="fa fa-exchange"></i>
                            </button>`;
                        }
                        return html;
                    }
                }
            ],
            drawCallback: () => {
                // Bind click events
                document.querySelectorAll('.view-details').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
                        if (id) self.viewDetails(parseInt(id));
                    });
                });
                document.querySelectorAll('.view-diff').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
                        if (id) self.viewDiff(parseInt(id));
                    });
                });
            }
        };
    }

    applyFilters(): void {
        if (this.dtElement && this.dtElement.dtInstance) {
            this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
                dtInstance.ajax.reload();
            });
        }
    }

    resetFilters(): void {
        this.dateFrom = '';
        this.dateTo = '';
        this.moduleFilter = '';
        this.actionFilter = '';
        this.userNameFilter = '';
        this.resultFilter = '';
        this.searchFilter = '';
        this.applyFilters();
    }

    viewDetails(id: number): void {
        this.auditLogsService.getAuditLogDetail(id).subscribe({
            next: (response) => {
                if (response.success && response.data) {
                    this.selectedLog = response.data;
                    this.showDetailModal = true;
                }
            },
            error: (err) => {
                console.error('Error loading log details:', err);
                Swal.fire('Eroare', 'Nu s-au putut încărca detaliile', 'error');
            }
        });
    }

    viewDiff(id: number): void {
        this.auditLogsService.getAuditLogDetail(id).subscribe({
            next: (response) => {
                if (response.success && response.data) {
                    const log = response.data;
                    const oldHtml = log.old_values ? `<pre class="bg-light p-2">${JSON.stringify(log.old_values, null, 2)}</pre>` : '<p class="text-muted">N/A</p>';
                    const newHtml = log.new_values ? `<pre class="bg-light p-2">${JSON.stringify(log.new_values, null, 2)}</pre>` : '<p class="text-muted">N/A</p>';

                    Swal.fire({
                        title: 'Diferențe',
                        html: `
                            <div class="row">
                                <div class="col-6">
                                    <h6>Valori Vechi</h6>
                                    ${oldHtml}
                                </div>
                                <div class="col-6">
                                    <h6>Valori Noi</h6>
                                    ${newHtml}
                                </div>
                            </div>
                        `,
                        width: '80%',
                        showCloseButton: true,
                        showConfirmButton: false
                    });
                }
            },
            error: (err) => {
                console.error('Error loading log diff:', err);
                Swal.fire('Eroare', 'Nu s-au putut încărca diferențele', 'error');
            }
        });
    }

    closeDetailModal(): void {
        this.showDetailModal = false;
        this.selectedLog = null;
    }

    exportCsv(): void {
        this.isExporting = true;
        const params: any = {};

        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
        if (this.moduleFilter) params.module = this.moduleFilter;
        if (this.actionFilter) params.action = this.actionFilter;
        if (this.userNameFilter) params.user_name = this.userNameFilter;
        if (this.resultFilter) params.result = this.resultFilter;
        if (this.searchFilter) params.search = this.searchFilter;

        this.auditLogsService.exportCsv(params).subscribe({
            next: (blob) => {
                this.isExporting = false;
                const filename = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
                this.auditLogsService.downloadFile(blob, filename);
            },
            error: (err) => {
                this.isExporting = false;
                console.error('Export error:', err);
                Swal.fire('Eroare', 'Exportul a eșuat', 'error');
            }
        });
    }

    formatJson(obj: any): string {
        if (!obj) return 'N/A';
        return JSON.stringify(obj, null, 2);
    }
}
